import { supabase } from "../services/supabaseClient";

const SESSION_STORAGE_KEY = "uniair_booking_funnel_session_id";
const QUEUE_STORAGE_KEY = "uniair_booking_funnel_queue_v2";
const DEFAULT_DEDUPE_WINDOW_MS = 1500;
const MAX_QUEUE_ITEMS = 100;
const MAX_QUEUE_AGE_MS = 7 * 24 * 60 * 60 * 1000;
const recentEvents = new Map();
let flushPromise = null;

const createUuid = () => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }

  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (character) => {
    const random = Math.floor(Math.random() * 16);
    const value = character === "x" ? random : (random & 0x3) | 0x8;
    return value.toString(16);
  });
};

const cleanReasonCode = (value) => {
  const normalized = String(value || "")
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9_]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 100);

  return normalized || null;
};

const safeNumericBookingId = (value) => {
  if (value === null || value === undefined || value === "") return null;

  const numeric = Number(value);
  return Number.isSafeInteger(numeric) && numeric > 0 ? numeric : null;
};

const readQueue = () => {
  if (typeof window === "undefined") return [];

  try {
    const parsed = JSON.parse(window.localStorage.getItem(QUEUE_STORAGE_KEY) || "[]");
    if (!Array.isArray(parsed)) return [];

    const cutoff = Date.now() - MAX_QUEUE_AGE_MS;
    return parsed
      .filter(
        (item) =>
          item &&
          item.clientEventId &&
          Number(item.createdAt || 0) >= cutoff,
      )
      .slice(-MAX_QUEUE_ITEMS);
  } catch {
    return [];
  }
};

const writeQueue = (queue) => {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.setItem(
      QUEUE_STORAGE_KEY,
      JSON.stringify((Array.isArray(queue) ? queue : []).slice(-MAX_QUEUE_ITEMS)),
    );
  } catch {
    // Analytics storage must never interrupt the booking flow.
  }
};

const isMissingV2Function = (error) => {
  const message = String(
    error?.message || error?.details || error?.hint || error || "",
  ).toLowerCase();

  return (
    error?.code === "PGRST202" ||
    message.includes("record_booking_funnel_event_v2") &&
      (message.includes("not found") || message.includes("schema cache"))
  );
};

const sendQueuedItem = async (item) => {
  const v2Result = await supabase.rpc("record_booking_funnel_event_v2", {
    p_event_name: item.eventName,
    p_reason_code: item.reasonCode,
    p_ride_mode: item.rideMode,
    p_page_source: item.pageSource,
    p_market_id: item.marketId,
    p_booking_id: item.bookingId,
    p_session_id: item.sessionId,
    p_client_event_id: item.clientEventId,
  });

  if (!v2Result.error) return { ok: true };

  // This fallback keeps deployments safe while SQL and frontend releases are
  // propagating. Once v2 exists, retries are idempotent through clientEventId.
  if (isMissingV2Function(v2Result.error)) {
    const v1Result = await supabase.rpc("record_booking_funnel_event_v1", {
      p_event_name: item.eventName,
      p_reason_code: item.reasonCode,
      p_ride_mode: item.rideMode,
      p_page_source: item.pageSource,
      p_market_id: item.marketId,
      p_booking_id: item.bookingId,
      p_session_id: item.sessionId,
    });

    if (!v1Result.error) return { ok: true };
    return { ok: false, error: v1Result.error };
  }

  return { ok: false, error: v2Result.error };
};

export const getBookingFunnelSessionId = () => {
  try {
    const existing = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (existing) return existing;

    const created = createUuid();
    sessionStorage.setItem(SESSION_STORAGE_KEY, created);
    return created;
  } catch {
    return createUuid();
  }
};

export const inferBookingFunnelReasonCode = (
  error,
  fallback = "UNKNOWN_ERROR",
) => {
  const message = String(
    error?.message || error?.details || error?.hint || error || "",
  ).toLowerCase();

  if (
    message.includes("reverification") ||
    message.includes("verification required")
  ) {
    return "VERIFICATION_REQUIRED";
  }

  if (
    message.includes("market") &&
    (message.includes("closed") || message.includes("not open"))
  ) {
    return "MARKET_CLOSED";
  }

  if (
    message.includes("location") ||
    message.includes("coordinate") ||
    message.includes("address") ||
    message.includes("pickup point") ||
    message.includes("dropoff point")
  ) {
    return "INVALID_LOCATION";
  }

  if (
    message.includes("driver") &&
    (message.includes("available") || message.includes("availability"))
  ) {
    return "NO_DRIVER_AVAILABLE";
  }

  if (
    message.includes("payment") ||
    message.includes("paystack") ||
    message.includes("card")
  ) {
    return "PAYMENT_FAILED";
  }

  if (
    message.includes("network") ||
    message.includes("fetch") ||
    message.includes("timeout") ||
    message.includes("connection") ||
    message.includes("failed to load resource")
  ) {
    return "NETWORK_ERROR";
  }

  return cleanReasonCode(fallback) || "UNKNOWN_ERROR";
};

export const flushBookingFunnelQueue = async () => {
  if (flushPromise) return flushPromise;

  flushPromise = (async () => {
    try {
      if (typeof navigator !== "undefined" && navigator.onLine === false) {
        return { sent: 0, remaining: readQueue().length };
      }

      const { data, error } = await supabase.auth.getSession();
      if (error || !data?.session?.user?.id) {
        return { sent: 0, remaining: readQueue().length };
      }

      const currentUserId = data.session.user.id;
      const queue = readQueue();
      const remaining = [];
      let sent = 0;

      for (const item of queue) {
        // Never attribute one browser user's queued event to a later login.
        if (item.userId && item.userId !== currentUserId) continue;

        const result = await sendQueuedItem(item);
        if (result.ok) {
          sent += 1;
          continue;
        }

        remaining.push({
          ...item,
          attempts: Number(item.attempts || 0) + 1,
          lastAttemptAt: Date.now(),
        });

        console.warn("Booking funnel event remains queued:", {
          eventName: item.eventName,
          message: result.error?.message,
          code: result.error?.code,
        });

        // Preserve event order and avoid hammering Supabase while offline or
        // during a temporary API outage.
        const unsentIndex = queue.indexOf(item);
        remaining.push(...queue.slice(unsentIndex + 1));
        break;
      }

      writeQueue(remaining);
      return { sent, remaining: remaining.length };
    } catch (error) {
      console.warn("Booking funnel queue flush failed:", error);
      return { sent: 0, remaining: readQueue().length };
    } finally {
      flushPromise = null;
    }
  })();

  return flushPromise;
};

/**
 * Records a safe booking-funnel step without blocking the passenger flow.
 * Events are first persisted locally, then flushed to an idempotent RPC.
 * Do not send addresses, card details, identity numbers, flight numbers,
 * notes, or any other free-form passenger information.
 */
export async function trackBookingFunnelEvent(
  eventName,
  {
    reasonCode = null,
    rideMode = null,
    pageSource = null,
    marketId = null,
    bookingId = null,
    sessionId = getBookingFunnelSessionId(),
    dedupeWindowMs = DEFAULT_DEDUPE_WINDOW_MS,
  } = {},
) {
  const normalizedEventName = String(eventName || "").trim().toLowerCase();
  if (!normalizedEventName) return;

  const normalizedReasonCode = cleanReasonCode(reasonCode);
  const normalizedBookingId = safeNumericBookingId(bookingId);
  const dedupeKey = [
    normalizedEventName,
    normalizedReasonCode || "",
    String(rideMode || "").toLowerCase(),
    String(pageSource || "").toLowerCase(),
    marketId || "",
    normalizedBookingId || "",
    sessionId || "",
  ].join("|");

  const now = Date.now();
  const lastRecordedAt = recentEvents.get(dedupeKey) || 0;
  if (dedupeWindowMs > 0 && now - lastRecordedAt < dedupeWindowMs) return;
  recentEvents.set(dedupeKey, now);

  try {
    const { data, error } = await supabase.auth.getSession();
    if (error || !data?.session?.user?.id) return;

    const item = {
      clientEventId: createUuid(),
      userId: data.session.user.id,
      eventName: normalizedEventName,
      reasonCode: normalizedReasonCode,
      rideMode: rideMode ? String(rideMode).toLowerCase().slice(0, 40) : null,
      pageSource: pageSource
        ? String(pageSource).toLowerCase().slice(0, 80)
        : null,
      marketId: marketId || null,
      bookingId: normalizedBookingId,
      sessionId: sessionId || null,
      createdAt: now,
      attempts: 0,
    };

    const queue = readQueue();
    queue.push(item);
    writeQueue(queue);

    await flushBookingFunnelQueue();
  } catch (error) {
    // Analytics must never interrupt the passenger experience.
    console.warn("Booking funnel tracking failed:", error);
  }
}

if (typeof window !== "undefined") {
  window.addEventListener("online", () => {
    void flushBookingFunnelQueue();
  });

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") {
      void flushBookingFunnelQueue();
    }
  });

  window.setTimeout(() => {
    void flushBookingFunnelQueue();
  }, 1000);
}
